import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoIosArrowBack } from 'react-icons/io';
import './employeeWorkingData.css';
import { WorkStatMnth } from './workStatMnth/workStatMnth';
import { OverTime } from './OverTime/OverTime';
import { WorkStatDay } from './workStatDay/workStatDay';
import WorkStatAll from './workStatAll/workStatAll';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import axiosInstance from '../../../../../utils/axiosInstance';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import { Col, Row } from 'react-bootstrap';
import { formatISOToLocalTime, normalizeDbTimestampToUtcIso } from '../../../../../utils/timeUtils';
import WorkDataDateRangeFilter from '../../../../../components/common/WorkDataDateRangeFilter';
import {
  filterWorkDetailsByDateRange,
  getLocalCalendarMonthBounds,
  resolveWorkFetchDateRange,
} from '../../../../../utils/workDataDateRange';

const EmployeeWorkingData = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { employeeId, employeeName, project } = location.state || {};

  const [employeeWorkDetails, setEmployeeWorkDetails] = useState([]);
  const [projEmpAssign, setProjEmpAssign] = useState(null);
  const [projectsList, setProjectsList] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [workRangeStart, setWorkRangeStart] = useState(() => getLocalCalendarMonthBounds().startDate);
  const [workRangeEnd, setWorkRangeEnd] = useState(() => getLocalCalendarMonthBounds().endDate);

  const loadedRange = useMemo(
    () => resolveWorkFetchDateRange(workRangeStart, workRangeEnd),
    [workRangeStart, workRangeEnd]
  );

  const displayWorkDetails = useMemo(
    () => filterWorkDetailsByDateRange(employeeWorkDetails, workRangeStart, workRangeEnd),
    [employeeWorkDetails, workRangeStart, workRangeEnd]
  );

  const projectForStatsFooters = useMemo(() => {
    if (!project) return project;
    return {
      ...project,
      startDate: loadedRange.startDate,
      endDate: loadedRange.endDate,
    };
  }, [project, loadedRange]);

  const monthNow = getLocalCalendarMonthBounds();
  const showResetToCurrentMonth =
    workRangeStart !== monthNow.startDate || workRangeEnd !== monthNow.endDate;

  useEffect(() => {
    const detectTimezone = () => {
      const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedZone !== userTimeZone) {
        setUserTimeZone(detectedZone);
      }
    };

    window.addEventListener('focus', detectTimezone);
    return () => {
      window.removeEventListener('focus', detectTimezone);
    };
  }, [userTimeZone]);

  // Format timezone for display
  const formatTimeZone = () => {
    try {
      const timeZoneAbbr = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timeZone: userTimeZone
      }).formatToParts().find(part => part.type === 'timeZoneName')?.value;

      return timeZoneAbbr || userTimeZone.split('/').pop();
    } catch (e) {
      return userTimeZone.split('/').pop();
    }
  };

  useEffect(() => {
    const fetchAssignmentId = async () => {
      try {
        const response = await axiosInstance.get('/admin/empProjRelation');
        const assignments = response.data;

        const projectId = project.id.toString();
        const employeeIdString = employeeId.toString();

        const assignment = assignments.find(
          (assign) =>
            assign.project_id.toString() === projectId &&
            assign.emp_id.toString() === employeeIdString
        );

        if (assignment) {
          setProjEmpAssign(assignment.assign_id);
        }

        const fetchProjectsList = async () => {
          try {
            const response = await axiosInstance.get('/admin/allProjectInfo');
            const allProjects = response.data;
            const assignId = assignment?.assign_id;

            // `allProjectInfo` returns rows from `project_info` (no assign_id).
            // Keep this list best-effort without crashing the app.
            const filtered = allProjects.filter((p) => {
              if (!assignId) return false;
              const pid = p.project_id ?? p.id;
              return String(pid) === String(project.id);
            });

            setProjectsList(filtered);
          } catch (error) {
            console.error('Error fetching projects list:', error);
          }
        };

        fetchProjectsList();
      } catch (error) {
        console.error('Error fetching assignments:', error);
      }
    };

    fetchAssignmentId();
  }, [project.id, employeeId]);

  useEffect(() => {
    const fetchEmployeeWorkDetails = async () => {
      try {
        const range = resolveWorkFetchDateRange(workRangeStart, workRangeEnd);
        const response = await axiosInstance.get('/admin/empProjWork', {
          params: { startDate: range.startDate, endDate: range.endDate },
        });
        const employeeWork = response.data;

        const filteredWorkDetails = employeeWork
          .filter(work => work.assign_id === projEmpAssign)
          .map(detail => ({
            ...detail,
            time_start: normalizeDbTimestampToUtcIso(detail.time_start, detail.work_date),
            time_stop: normalizeDbTimestampToUtcIso(detail.time_stop, detail.work_date)
          }));

        setEmployeeWorkDetails(filteredWorkDetails);
      } catch (error) {
        console.error('Error fetching employee work details:', error);
      }
    };

    if (projEmpAssign) {
      fetchEmployeeWorkDetails();
    }
  }, [projEmpAssign, workRangeStart, workRangeEnd]);

  useEffect(() => {
    if (projEmpAssign) {
      const filteredProjects = projectsList.filter(
        (proj) => proj.assign_id === projEmpAssign
      );
      setFilteredProjects(filteredProjects);
    }
  }, [projectsList, projEmpAssign]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownloadReport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Work Details ${employeeName} for ${project.name}`);

    worksheet.views = [{ showGridLines: false }];

    // Add title
    worksheet.insertRow(1, []);
    let titleRow = worksheet.insertRow(2, ['', '', '', '', 'Timesheet', '', '', '']);
    titleRow.eachCell((cell, colNumber) => {
      if (colNumber === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } };
      } else {
        cell.alignment = { horizontal: 'center' };
        cell.font = { size: 25, bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '9BC2E6' } };
      }
    });

    // Add timezone info
    worksheet.insertRow(3, []);
    // worksheet.insertRow(4, ['', '', `All times shown in ${formatTimeZone()}`]);

    // Add metadata
    const formattedDate = format(new Date(), 'dd/MM/yyyy');
    worksheet.insertRow(4, ['', { richText: [{ text: t("empForm_namePlace"), font: { bold: true } }] }, employeeName, '']);
    worksheet.insertRow(5, ['', { richText: [{ text: t("addproject_name"), font: { bold: true } }] }, project.name, '']);
    worksheet.insertRow(6, ['', { richText: [{ text: 'Downloaded On:', font: { bold: true } }] }, formattedDate, '']);
    worksheet.insertRow(7, []);


    const today = new Date().toLocaleDateString();
    const thisMonth = new Date().toLocaleString('default', { month: 'long' });
    let totalDurationToday = 0;
    let totalDurationThisMonth = 0;
    let totalOvertime = 0;
    let totalWorking = 0;

    displayWorkDetails.forEach(detail => {
      if (detail && detail.work_date && new Date(detail.work_date).toLocaleDateString() === today) {
        if (detail.work_duration) {
          const [hours, minutes, seconds] = detail.work_duration.split(':').map(Number);
          totalDurationToday += hours * 3600 + minutes * 60 + seconds;
        }
      }

      if (detail && detail.work_date && new Date(detail.work_date).toLocaleString('default', { month: 'long' }) === thisMonth) {
        if (detail.work_duration) {
          const [hours, minutes, seconds] = detail.work_duration.split(':').map(Number);
          totalDurationThisMonth += hours * 3600 + minutes * 60 + seconds;
        }
      }

      if (detail && detail.overtime === 'Overtime' && detail.work_duration) {
        const [hours, minutes, seconds] = detail.work_duration.split(':').map(Number);
        totalOvertime += hours * 3600 + minutes * 60 + seconds;
      }

      if (detail.work_duration) {
        const [hours, minutes, seconds] = detail.work_duration.split(':').map(Number);
        totalWorking += hours * 3600 + minutes * 60 + seconds;
      }
    });

    const formatDuration = (totalSeconds) => {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    // Add summary rows
    const detailsRows = [
      ['',
        { richText: [{ text: t("projectDetails_todayWork"), font: { bold: true } }] },
        { richText: [{ text: t("employeeWorkDetails_month"), font: { bold: true } }] },
        { richText: [{ text: t("projectDetails_totalOvertime"), font: { bold: true } }] },
        { richText: [{ text: t("projectDetails_totalWorking"), font: { bold: true } }] }
      ],
      ['', formatDuration(totalDurationToday), formatDuration(totalDurationThisMonth),
        formatDuration(totalOvertime), formatDuration(totalWorking)],
    ];

    detailsRows.forEach((rowData, index) => {
      const row = worksheet.addRow(rowData);
      row.eachCell((cell, colNumber) => {
        if (colNumber > 1) {
          cell.alignment = { horizontal: 'left' };
          if (index === 0) cell.font = { bold: true };
        }
      });
    });

    worksheet.addRow([]);

    // Add work details
    const headers = [
      t("employeeWorkDetails_SrNo"),
      t("employeeWorkDetails_date"),
      t("employeeWorkDetails_startTime"),
      t("employeeWorkDetails_EndTime"),
      t("employeeWorkDetails_duration"),
      'Overtime Status',
      t("employeeWorkDetails_comment")
    ];

    const headerRow = worksheet.addRow(['', ...headers]);
    headerRow.eachCell((cell, colNumber) => {
      if (colNumber > 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } };
        cell.font = { bold: true, size: 12, color: { argb: '333333' } };
        cell.alignment = { horizontal: 'center' };
      }
    });

    displayWorkDetails.forEach((detail, index) => {
      const row = worksheet.addRow([
        '',
        index + 1,
        format(detail.work_date, 'dd/MM/yyyy'),
        formatISOToLocalTime(detail.time_start),
        detail.time_stop ? formatISOToLocalTime(detail.time_stop) : 'working',
        detail.work_duration || 'N/A',
        detail.overtime,
        detail.comments || ''
      ]);

      row.eachCell((cell, colNumber) => {
        if (colNumber > 1) {
          cell.alignment = { horizontal: 'center' };
          if (colNumber === 8) cell.alignment = { wrapText: true };
        }
      });
    });

    // Set column widths
    worksheet.columns.forEach((column, colNumber) => {
      column.width = colNumber === 8 ? 50 : 20;
    });

    // Save the file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Work Details ${employeeName} for ${project.name}.xlsx`
    );
  };

  const handleProjectChange = (newProject) => {
    navigate(`/employeeWorkingData/${employeeId}`, { state: { ...location.state, project: newProject } });
    setDropdownVisible(false);
  };

  return (
    <div className="EmployeeWorkingData-body">
      <div className='buttonComtainer'>
        <button className="backButton" onClick={handleBack}>
          <IoIosArrowBack style={{ marginRight: '5px' }} /> {t("projectDetails_back")}
        </button>
        <button className="downloadButton" onClick={handleDownloadReport}>
          {t("employeeWorkDetails_reportDown")}
        </button>
      </div>

      <div className="EmployeeWorkingData-header">
        <span className="EmployeeWorkingData-headerTitle">
          {t("employeeWorkDetails_nameTitle")} {employeeName}
        </span>
        <br />
        <div className="EmployeeWorkingData-subtitle">
          <h3>{project.name}</h3>
        </div>
      </div>

      {/* <div className='timezone-info'>
        ({t("Times shown in")} {formatTimeZone()})
      </div> */}

      <div className='d-flex justify-content-end'>
        <WorkDataDateRangeFilter
          startDate={workRangeStart}
          endDate={workRangeEnd}
          onStartDateChange={setWorkRangeStart}
          onEndDateChange={setWorkRangeEnd}
          showClearButton={showResetToCurrentMonth}
          labels={{ clear: 'This month' }}
          onClear={() => {
            const b = getLocalCalendarMonthBounds();
            setWorkRangeStart(b.startDate);
            setWorkRangeEnd(b.endDate);
          }}
        />
      </div>

      <div className="EmployeeWorkingData-details">
        <div className='Empstats'>
          <Row>
            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
              <WorkStatDay employeeWorkDetails={displayWorkDetails} />
            </Col>
            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
              <WorkStatMnth employeeWorkDetails={displayWorkDetails} />
            </Col>
            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
              <OverTime employeeWorkDetails={displayWorkDetails} project={projectForStatsFooters} />
            </Col>
            <Col lg={3} md={6} sm={12} style={{ margin: '10px 0' }}>
              <WorkStatAll employeeWorkDetails={displayWorkDetails} project={projectForStatsFooters} />
            </Col>
          </Row>
        </div>

        <div className='legend-body'>
          <p className='TableTitle'>{t("employeeWorkDetails_workTitle")}</p>
          <div className="legend">
            <div className="legend-item">
              <span className="legend-color overtime-color"></span>
              <span>{t("employeeWorkDetails_overtime")}</span>
            </div>
            <div className="legend-item">
              <span className="legend-color regular-color"></span>
              <span>Regular</span>
            </div>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ marginTop: '30px' }}>
            <thead>
              <tr>
                <th>{t("employeeWorkDetails_SrNo")}</th>
                <th>{t("employeeWorkDetails_date")}</th>
                <th>{t("employeeWorkDetails_startTime")}</th>
                <th>{t("employeeWorkDetails_EndTime")}</th>
                <th>{t("employeeWorkDetails_duration")}</th>
                <th>{t("employeeWorkDetails_comment")}</th>
              </tr>
            </thead>
            <tbody>
              {displayWorkDetails && displayWorkDetails.length > 0 ? (
                displayWorkDetails.map((detail, index) => (
                  <tr key={index} className={detail.overtime === 'Overtime' ? 'overtime-row' : 'rugular-row'}>
                    <td>{index + 1}</td>
                    <td>{format(new Date(detail.work_date), 'dd/MM/yyyy')}</td>
                    <td>{formatISOToLocalTime(detail.time_start)}</td>
                    <td>{detail.time_stop ? formatISOToLocalTime(detail.time_stop) : '-'}</td>
                    <td>{detail.work_duration || '-'}</td>
                    <td>
                      <span className="comment" title={detail.comments}>
                        {detail.comments && detail.comments.length > 20
                          ? `${detail.comments.slice(0, 20)}...`
                          : detail.comments || '-'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : employeeWorkDetails.length > 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: 'lightgray', opacity: '0.6' }}>
                    No contributions in this date range
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: 'lightgray', opacity: '0.6' }}>
                    {t("No contributions yet")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmployeeWorkingData;
